import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Switch,
  MenuItem,
  Chip,
  Stack,
} from '@mui/material';
import {
  companyConfigService,
  type CreateCompanyRequest,
  type PixKeyType,
  type CompanyPlan,
} from '../services/companyConfigService';
import { authService } from '../services';
import {
  COMPANY_PLAN_OPTIONS,
  derivePlanCapabilities,
} from '../constants/companyPlans';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const formatCnpj = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12)
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

const formatCep = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatWhatsapp = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

interface CompanyForm {
  cnpj: string;
  razaoSocial: string;
  slug: string;
  whatsappPhone: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  cep: string;
  complement: string;
  workingDays: number[];
  message: string;
  startDate: string;
  plan: CompanyPlan;
}

type ModulesForm = {
  paymentsEnabled: boolean;
  paymentsPixKey: string;
  paymentsPixKeyType: PixKeyType | '';
  inventoryEnabled: boolean;
};

const PIX_KEY_TYPES: PixKeyType[] = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'];

const CompanyCreate: React.FC = () => {
  const userRole = authService.getUserRole();
  const [companyForm, setCompanyForm] = useState<CompanyForm>({
    cnpj: '',
    razaoSocial: '',
    slug: '',
    whatsappPhone: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    cep: '',
    complement: '',
    workingDays: [2, 3, 4, 5, 6, 0],
    message: '',
    startDate: '',
    plan: 'VITRINE_APP',
  });
  const initialCaps = derivePlanCapabilities('VITRINE_APP');
  const [modulesForm, setModulesForm] = useState<ModulesForm>({
    paymentsEnabled: initialCaps.paymentsEnabled,
    paymentsPixKey: '',
    paymentsPixKeyType: '',
    inventoryEnabled: initialCaps.inventoryEnabled,
  });
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const planCapabilities = useMemo(() => derivePlanCapabilities(companyForm.plan), [companyForm.plan]);
  const selectedPlan = useMemo(
    () => COMPANY_PLAN_OPTIONS.find((option) => option.value === companyForm.plan) ?? COMPANY_PLAN_OPTIONS[0],
    [companyForm.plan]
  );

  const handleModulesChange = (field: keyof ModulesForm, value: string | boolean) => {
    if (field === 'paymentsEnabled' && !planCapabilities.paymentsEnabled) {
      return;
    }
    if (field === 'inventoryEnabled' && !planCapabilities.inventoryEnabled) {
      return;
    }

    setModulesForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const slugify = (value: string): string => {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleChange = (field: keyof CompanyForm, value: string) => {
    setCompanyForm((prev) => {
      let nextValue = value;
      if (field === 'cnpj' || field === 'whatsappPhone' || field === 'cep') {
        let digits = value.replace(/\D/g, '');
        if (field === 'cnpj') digits = digits.slice(0, 14);
        if (field === 'cep') digits = digits.slice(0, 8);
        if (field === 'whatsappPhone') digits = digits.slice(0, 11);
        nextValue = digits;
      }
      const next: CompanyForm = { ...prev, [field]: nextValue } as CompanyForm;
      if (field === 'razaoSocial') {
        const text = String(value || '');
        next.slug = text ? slugify(text) : '';
      }

      if (field === 'plan') {
        const capabilities = derivePlanCapabilities(next.plan);
        setModulesForm((prevModules) => ({
          ...prevModules,
          paymentsEnabled: capabilities.paymentsEnabled,
          inventoryEnabled: capabilities.inventoryEnabled,
          paymentsPixKey: capabilities.paymentsEnabled ? prevModules.paymentsPixKey : '',
          paymentsPixKeyType: capabilities.paymentsEnabled ? prevModules.paymentsPixKeyType : '',
        }));
      }

      return next;
    });
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleToggleWorkingDay = (day: number) => {
    setCompanyForm((prev) => {
      const exists = prev.workingDays.includes(day);
      const workingDays = exists ? prev.workingDays.filter((d) => d !== day) : [...prev.workingDays, day];
      return { ...prev, workingDays };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    const newErrors: { [key: string]: string | undefined } = {};

    const cnpjDigits = companyForm.cnpj.replace(/\D/g, '');
    if (!cnpjDigits || cnpjDigits.length !== 14) newErrors.cnpj = 'CNPJ deve conter exatamente 14 dígitos.';

    const whatsappDigits = companyForm.whatsappPhone.replace(/\D/g, '');
    if (whatsappDigits && whatsappDigits.length !== 10 && whatsappDigits.length !== 11) {
      newErrors.whatsappPhone = 'WhatsApp deve ter 10 ou 11 dígitos (com DDD).';
    }

    const cepDigits = companyForm.cep.replace(/\D/g, '');
    if (cepDigits && cepDigits.length !== 8) newErrors.cep = 'CEP deve conter exatamente 8 dígitos.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    const payload: CreateCompanyRequest = {
      cnpj: cnpjDigits,
      razaoSocial: companyForm.razaoSocial || undefined,
      slug: companyForm.slug || undefined,
      whatsappPhone: whatsappDigits || undefined,
      endereco: {
        rua: companyForm.street,
        numero: companyForm.number,
        bairro: companyForm.neighborhood,
        cidade: companyForm.city,
        estado: companyForm.state,
        cep: cepDigits,
        complemento: companyForm.complement || undefined,
      },
      message: companyForm.message || undefined,
      dataInicioUsoPlataforma: companyForm.startDate ? new Date(companyForm.startDate).toISOString() : undefined,
      diasFuncionamento: companyForm.workingDays,
      plan: companyForm.plan,
      modules: {
        payments: {
          enabled: modulesForm.paymentsEnabled,
          pixKey: modulesForm.paymentsPixKey.trim() || undefined,
          pixKeyType: modulesForm.paymentsPixKeyType || undefined,
        },
        inventory: {
          enabled: modulesForm.inventoryEnabled,
        },
      },
    };

    try {
      await companyConfigService.createCompanyWithAdmin(payload);
      setSuccess('Empresa cadastrada com sucesso. Agora cadastre os usuários dessa empresa no módulo "Usuários (Master)", informando o mesmo CNPJ.');
    } catch (err: any) {
      setError(err.message || 'Erro ao cadastrar empresa');
    } finally {
      setLoading(false);
    }
  };

  if (userRole !== 'DEVELOPER') {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Apenas usuários DEVELOPER podem cadastrar novas empresas.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Cadastro de nova empresa
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Crie uma nova empresa para o Better Delivery e um usuário admin vinculado a ela.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Razão social"
              value={companyForm.razaoSocial}
              onChange={(e) => handleChange('razaoSocial', e.target.value)}
              fullWidth
            />
            <TextField
              label="Slug público (URL da loja)"
              value={companyForm.slug}
              helperText="Será usado na URL pública do delivery. Use apenas letras minúsculas, números e hífens."
              disabled
              fullWidth
            />
            <TextField
              label="CNPJ"
              value={formatCnpj(companyForm.cnpj)}
              onChange={(e) => handleChange('cnpj', e.target.value)}
              fullWidth
              required
              error={!!errors.cnpj}
              helperText={errors.cnpj}
            />

            <TextField
              label="WhatsApp da empresa (somente números, com DDD)"
              value={formatWhatsapp(companyForm.whatsappPhone)}
              onChange={(e) => handleChange('whatsappPhone', e.target.value)}
              fullWidth
              error={!!errors.whatsappPhone}
              helperText={errors.whatsappPhone}
            />

            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
              <TextField
                select
                label="Plano contratado"
                value={companyForm.plan}
                onChange={(e) => handleChange('plan', e.target.value as CompanyPlan)}
                sx={{ minWidth: { md: 260 } }}
              >
                {COMPANY_PLAN_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <Chip
                label={selectedPlan.focus}
                color={companyForm.plan === 'WHITE_LABEL_BUSINESS' ? 'secondary' : 'default'}
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, fontWeight: 600 }}
              />
            </Box>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {selectedPlan.monthlyPrice}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
                  {selectedPlan.setupFee}
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedPlan.description}
              </Typography>
              <Stack component="ul" spacing={1.25} sx={{ mt: 2, pl: 0, listStyle: 'none' }}>
                {selectedPlan.features.map((feature) => (
                  <Stack key={feature} component="li" direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 18, mt: 0.25 }} />
                    <Typography variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Rua"
                value={companyForm.street}
                onChange={(e) => handleChange('street', e.target.value)}
                fullWidth
              />
              <TextField
                label="Número"
                value={companyForm.number}
                onChange={(e) => handleChange('number', e.target.value)}
                sx={{ width: { xs: '100%', sm: '160px' } }}
              />
            </Box>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Bairro"
                value={companyForm.neighborhood}
                onChange={(e) => handleChange('neighborhood', e.target.value)}
                fullWidth
              />
              <TextField
                label="Cidade"
                value={companyForm.city}
                onChange={(e) => handleChange('city', e.target.value)}
                fullWidth
              />
            </Box>

            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                label="Estado"
                value={companyForm.state}
                onChange={(e) => handleChange('state', e.target.value)}
                sx={{ width: { xs: '100%', sm: '120px' } }}
              />
              <TextField
                label="CEP"
                value={formatCep(companyForm.cep)}
                onChange={(e) => handleChange('cep', e.target.value)}
                sx={{ width: { xs: '100%', sm: '200px' } }}
                error={!!errors.cep}
                helperText={errors.cep}
              />
              <TextField
                label="Complemento"
                value={companyForm.complement}
                onChange={(e) => handleChange('complement', e.target.value)}
                fullWidth
              />
            </Box>

            <TextField
              label="Mensagem da loja / aviso (opcional)"
              value={companyForm.message}
              onChange={(e) => handleChange('message', e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Início de uso da plataforma
              </Typography>
              <TextField
                type="date"
                value={companyForm.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Dias de funcionamento (delivery)
              </Typography>
              <FormGroup row>
                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={companyForm.workingDays.includes(day)}
                        onChange={() => handleToggleWorkingDay(day)}
                      />
                    }
                    label={['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][day]}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Módulos da plataforma
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">Pagamentos online</Typography>
                    <Switch
                      checked={modulesForm.paymentsEnabled}
                      onChange={(e) => handleModulesChange('paymentsEnabled', e.target.checked)}
                      color="primary"
                      disabled={!planCapabilities.paymentsEnabled}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Configure seu provedor de pagamento online e personalize a chave PIX que receberá os repasses.
                  </Typography>
                  <TextField
                    label="Chave PIX para repasse"
                    value={modulesForm.paymentsPixKey}
                    onChange={(e) => handleModulesChange('paymentsPixKey', e.target.value)}
                    fullWidth
                    disabled={!modulesForm.paymentsEnabled}
                  />
                  <TextField
                    select
                    label="Tipo da chave PIX"
                    value={modulesForm.paymentsPixKeyType}
                    onChange={(e) => handleModulesChange('paymentsPixKeyType', e.target.value as PixKeyType | '')}
                    disabled={!modulesForm.paymentsEnabled}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>Selecionar</em>
                    </MenuItem>
                    {PIX_KEY_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">Controle de estoque</Typography>
                  <Switch
                    checked={modulesForm.inventoryEnabled}
                    onChange={(e) => handleModulesChange('inventoryEnabled', e.target.checked)}
                    color="secondary"
                    disabled={!planCapabilities.inventoryEnabled}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Permite lançar saídas e acompanhar o saldo de estoque quando o plano contratado inclui o módulo.
                </Typography>
              </Paper>
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Salvando...' : 'Cadastrar empresa'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CompanyCreate;
